import { useEffect, useRef } from 'react'
import { useSetRecoilState } from 'recoil'
import eventBus from '@/services/eventBus'
import organizationsAtom, { IOrganizationsState } from '@/pages/Organizations/organizations.atom.ts'
import recoilStateUpdaters from '@/recoil/recoil.stateUpdaters.ts'
import { produce } from 'immer'
import { IUser } from '@/pages/Users/users.types.ts'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'

const useOrganizationsEventsSubscriptions = () => {
  const setState = useSetRecoilState(organizationsAtom)

  // Поскольку сообщение может придти И по веб-сокетам И локально, сохраняем список обработанных сообщений
  const handledUserMessagesSet = useRef(new Set<IUser['id']>())
  const handledEquipmentMessagesSet = useRef(new Set<IEquipment['id']>())

  useEffect(() => {
    const userCreatedSub = eventBus.sub('users.created', (user) => {
      if (!user.organizationId || handledUserMessagesSet.current.has(user.id)) return
      handledUserMessagesSet.current.add(user.id)

      setState((prev) =>
        produce(prev, (draft: IOrganizationsState) => {
          const index = draft.items.findIndex((o) => o.id === user.organizationId)
          if (index === -1) return draft

          draft.items[index]._count.users++
          return draft
        })
      )
    })

    const equipmentCreatedSub = eventBus.sub('equipment.created', (equipment) => {
      if (!equipment.organizations?.length || handledEquipmentMessagesSet.current.has(equipment.id)) return
      handledEquipmentMessagesSet.current.add(equipment.id)

      setState((prev) =>
        produce(prev, (draft: IOrganizationsState) => {
          for (const org of equipment.organizations) {
            const index = draft.items.findIndex((o) => o.id === org.organization.id)
            if (index === -1) continue

            draft.items[index]._count.organizationsOnEquipment++
          }

          return draft
        })
      )
    })

    const createSub = eventBus.sub('organizations.created', (item) => {
      recoilStateUpdaters.itemCreated(setState, item)
    })

    const updateSub = eventBus.sub('organizations.updated', (item) => {
      recoilStateUpdaters.itemUpdated(setState, item)
    })

    const removeSub = eventBus.sub('organizations.deleted', (id) => {
      recoilStateUpdaters.itemDeleted(setState, id)
    })

    return () => {
      createSub()
      updateSub()
      removeSub()
      userCreatedSub()
      equipmentCreatedSub()
    }
  }, [])
}

export default useOrganizationsEventsSubscriptions
